import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  WarningCircle,
  ArrowsClockwise,
  Robot,
  Megaphone,
  Gear,
  Storefront,
  InstagramLogo,
  FacebookLogo,
  WhatsappLogo,
  ShoppingBag,
  LinkSimple,
} from '@phosphor-icons/react';
import {
  apiClient,
  API_ENDPOINTS,
  useAuthStore,
  useNotificationStore,
} from '@dukene/shared';
import { MetaSyncStatusPanel } from '../components/settings/MetaSyncStatusPanel';
import BotFlowsList from './whatsapp/BotFlowsList';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { confirmAction } from '../components/modals/ConfirmModal';

function AssetIcon({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return <img src={src} alt={alt} className={className} />;
}

interface StoreSettings {
  metaSetupCompleted?: boolean;
  metaSetupCompletedAt?: string;
  metaSetupMode?: 'shop_and_ads' | 'ads_only';
  metaTermsAgreed?: boolean;
  [key: string]: any;
}

interface Store {
  id: string;
  name: string;
  country?: string;
  currency?: string;
  metaCommerceEnabled?: boolean;
  settings: StoreSettings | null;
}

interface MetaSyncStatus {
  lastSyncedAt: string | null;
  syncStatus: 'PENDING' | 'SYNCING' | 'SYNCED' | 'FAILED' | 'DELETED';
  lastError: string | null;
  metaCatalogId: string | null;
  metaBusinessId: string | null;
  catalogName: string | null;
  pageName: string | null;
  pageId: string | null;
  instagramLinked: boolean;
  instagramId: string | null;
  whatsappLinked: boolean;
  whatsappId: string | null;
  whatsappPhoneId: string | null;
  stats: {
    total: number;
    synced: number;
    failed: number;
  };
  isConnected: boolean;
}

interface MetaCatalog {
  id: string;
  name?: string;
  product_count?: number;
}

interface MetaCatalogResponse {
  catalogs: MetaCatalog[];
  selectedCatalogId: string | null;
  selectedCatalogName: string | null;
}

type TabType = 'overview' | 'setup' | 'marketing' | 'bot_flows' | 'settings';

export default function MetaCommerce() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const { addNotification } = useNotificationStore();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isCatalogDialogOpen, setIsCatalogDialogOpen] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(false);

  const { data: storeResponse } = useQuery<{ data: Store }>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: () => apiClient.get<{ data: Store }>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });

  const { data: status } = useQuery<MetaSyncStatus>({
    queryKey: ['meta-sync-status', activeStoreId],
    queryFn: () => apiClient.get<MetaSyncStatus>(API_ENDPOINTS.META.SYNC_STATUS),
    enabled: !!activeStoreId,
  });

  const { data: catalogResponse } = useQuery<MetaCatalogResponse>({
    queryKey: ['meta-catalogs', activeStoreId],
    queryFn: () => apiClient.get<MetaCatalogResponse>(API_ENDPOINTS.META.CATALOGS),
    enabled: !!activeStoreId && !!status?.isConnected && !status?.metaCatalogId,
  });

  useEffect(() => {
    const connected = searchParams.get('meta_connected');
    const error = searchParams.get('meta_error');
    const selectionRequired = searchParams.get('meta_catalog_selection_required');

    if (connected === 'true') {
      addNotification({
        type: 'success',
        title: 'Meta Connected',
        message: 'Your Meta Commerce assets have been successfully linked.',
      });
      setSearchParams(
        selectionRequired === 'true' ? { meta_catalog_selection_required: 'true' } : {},
        { replace: true }
      );
    } else if (error === 'true') {
      addNotification({
        type: 'error',
        title: 'Connection Failed',
        message: 'Could not link Meta Commerce assets. Please try again.',
      });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, addNotification]);

  useEffect(() => {
    const selectionRequired = searchParams.get('meta_catalog_selection_required') === 'true';
    if (selectionRequired && catalogResponse?.catalogs?.length) {
      setIsCatalogDialogOpen(true);
      setSelectedCatalogId(catalogResponse.selectedCatalogId || catalogResponse.catalogs[0]?.id || '');
    }
  }, [searchParams, catalogResponse]);

  const selectCatalogMutation = useMutation({
    mutationFn: (payload: { catalogId: string; catalogName?: string }) =>
      apiClient.post(API_ENDPOINTS.META.SELECT_CATALOG, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
      await queryClient.invalidateQueries({ queryKey: ['meta-catalogs', activeStoreId] });
      setIsCatalogDialogOpen(false);
      setSearchParams({}, { replace: true });
      addNotification({
        type: 'success',
        title: 'Catalog Selected',
        message: 'Your Meta catalog selection has been saved.',
      });
    },
  });

  const createCatalogMutation = useMutation({
    mutationFn: (payload: { catalogName?: string }) =>
      apiClient.post(API_ENDPOINTS.META.CREATE_CATALOG, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
      await queryClient.invalidateQueries({ queryKey: ['meta-catalogs', activeStoreId] });
      setIsCatalogDialogOpen(false);
      setSearchParams({}, { replace: true });
      addNotification({
        type: 'success',
        title: 'Catalog Created & Linked',
        message: 'Your new Meta product catalog has been created automatically and linked to your store.',
      });
    },
    onError: (err: any) => {
      addNotification({
        type: 'error',
        title: 'Creation Failed',
        message: err?.response?.data?.error || err?.message || 'Could not automatically create product catalog.',
      });
    },
  });

  const handleCatalogDialogOpenChange = (open: boolean) => {
    setIsCatalogDialogOpen(open);

    if (!open) {
      setSearchParams({}, { replace: true });
    }
  };

  const linkWhatsappMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.META.LINK_WHATSAPP),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
      addNotification({
        type: 'success',
        title: 'WhatsApp Linked',
        message: 'Your WhatsApp Business phone number is now connected.',
      });
    },
  });

  const linkInstagramMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.META.LINK_INSTAGRAM),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
      addNotification({
        type: 'success',
        title: 'Instagram Linked',
        message: 'Your Instagram account is now connected to your product catalog. Products will appear for tagging.',
      });
    },
    onError: (err: any) => {
      addNotification({
        type: 'error',
        title: 'Instagram Link Failed',
        message: err?.response?.data?.error || err?.message || 'Could not link Instagram account.',
      });
    },
  });

  const unlinkWhatsappMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.META.UNLINK_WHATSAPP),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
      addNotification({
        type: 'success',
        title: 'WhatsApp Disconnected',
        message: 'Your WhatsApp Business phone number was unlinked.',
      });
    },
  });

  const disconnectMetaMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.META.DISCONNECT, { storeId: activeStoreId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['store-by-id', activeStoreId] });
      addNotification({
        type: 'success',
        title: 'Meta Commerce Disconnected',
        message: 'Your Facebook and Instagram assets have been disconnected.',
      });
    },
  });

  const handleConnectMeta = async () => {
    try {
      setIsConnecting(true);
      const response = await apiClient.get<{ authUrl: string }>(API_ENDPOINTS.META.AUTH, {
        params: {
          storeId: activeStoreId,
          returnTo: '/meta-commerce',
        },
      });
      if (response?.authUrl) {
        window.location.href = response.authUrl;
      } else {
        throw new Error('No authorization URL returned from server.');
      }
    } catch (error: any) {
      setIsConnecting(false);
      addNotification({
        type: 'error',
        title: 'Connection Error',
        message: error?.message || 'Failed to initiate Meta connection.',
      });
    }
  };

  const isMetaLinked = !!status?.isConnected;
  const isSetupCompleted = !!storeResponse?.data?.settings?.metaSetupCompleted || (isMetaLinked && !!status?.metaCatalogId);
  const isWhatsAppConnected = !!status?.whatsappLinked;
  const availableCatalogs = catalogResponse?.catalogs || [];
  const needsCatalogSelection = isMetaLinked && !status?.metaCatalogId;

  return (
    <div className="space-y-5">
      {/* Polaris High-Density Top Card */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center text-[#006838] shrink-0">
              <AssetIcon src="/icons/meta-icon.svg" alt="Meta" className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-[#202223] dark:text-white">
                  Facebook & Instagram Sales Channel
                </h1>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    isMetaLinked
                      ? 'bg-[#E3F1DF] text-[#006838]'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {isMetaLinked ? 'CONNECTED' : 'NOT CONNECTED'}
                </span>
              </div>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                {isMetaLinked
                  ? `Active connection: Business ID ${status?.metaBusinessId || 'Linked'} | Catalog ID ${status?.metaCatalogId || 'Not Selected'}`
                  : 'Publish products across Facebook Marketplace and Instagram Shops'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isMetaLinked ? (
              <button
                type="button"
                onClick={async () => {
                  const confirmed = await confirmAction({
                    title: 'Disconnect Meta Commerce',
                    message: 'Are you sure you want to disconnect your Meta assets? Automatic catalog synchronization will stop.',
                    confirmText: 'Disconnect',
                    cancelText: 'Cancel',
                  });
                  if (confirmed) {
                    disconnectMetaMutation.mutate();
                  }
                }}
                disabled={disconnectMetaMutation.isPending}
                className="h-8 px-3 rounded-lg border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30 text-xs font-semibold text-red-600 dark:text-red-400 transition-colors"
              >
                {disconnectMetaMutation.isPending ? 'Disconnecting...' : 'Disconnect Account'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConnectMeta}
                disabled={!activeStoreId || isConnecting}
                className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors shadow-xs"
              >
                {isConnecting ? 'Connecting...' : 'Connect Meta Account'}
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] })
              }
              className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors"
            >
              <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* High-Density Navigation Tabs */}
        <div className="flex flex-wrap items-center gap-1 border-t border-[#E1E4E8] dark:border-slate-800 pt-3 text-xs font-semibold">
          {[
            { id: 'overview', label: 'Overview', icon: Storefront },
            {
              id: 'setup',
              label: 'Catalog & Account Setup',
              badge: isSetupCompleted ? 'Completed' : 'Action Needed',
            },
            { id: 'marketing', label: 'Marketing & Dynamic Ads', icon: Megaphone },
            { id: 'bot_flows', label: 'WhatsApp Bot Flows', icon: Robot, newBadge: true },
            { id: 'settings', label: 'Channel Settings', icon: Gear },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`h-8 px-3 rounded-lg inline-flex items-center gap-1.5 transition-all ${
                  active
                    ? 'bg-[#202223] dark:bg-white text-white dark:text-slate-900 shadow-xs font-bold'
                    : 'text-[#5C5F62] hover:bg-[#F6F6F7] dark:hover:bg-slate-800 hover:text-[#202223] dark:hover:text-white'
                }`}
              >
                {Icon && <Icon weight="bold" className="w-3.5 h-3.5" />}
                <span>{tab.label}</span>
                {tab.newBadge && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-[9px] font-bold">
                    NEW
                  </span>
                )}
                {tab.badge && (
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] ${
                      tab.badge === 'Completed'
                        ? 'bg-[#E3F1DF] text-[#006838]'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {!isMetaLinked && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <WarningCircle weight="fill" className="w-5 h-5 text-amber-600 shrink-0" />
                <div>
                  <h3 className="text-xs font-bold text-amber-900 dark:text-amber-200">
                    Facebook & Instagram connection required
                  </h3>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    Connect your Facebook Business Manager to enable automatic product syncing and live Instagram tags.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('setup')}
                className="h-8 px-4 rounded-lg bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-xs font-bold shrink-0"
              >
                Start Setup
              </button>
            </div>
          )}

          {needsCatalogSelection && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <WarningCircle weight="fill" className="w-5 h-5 text-amber-600 shrink-0" />
                <div>
                  <h3 className="text-xs font-bold text-amber-900 dark:text-amber-200">
                    Product Catalog Selection Needed
                  </h3>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    Your Meta account is connected, but no catalog is selected yet. Choose or create a catalog to enable product syncing.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCatalogDialogOpen(true)}
                className="h-8 px-4 rounded-lg bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-xs font-bold shrink-0"
              >
                Choose Catalog
              </button>
            </div>
          )}

          <MetaSyncStatusPanel />
        </div>
      )}

      {/* TAB 2: SETUP */}
      {activeTab === 'setup' && (
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-[#202223] dark:text-white">
                  Facebook &amp; Instagram Account Assets
                </h2>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    isMetaLinked
                      ? 'bg-[#E3F1DF] text-[#006838]'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {isMetaLinked ? 'CONNECTED' : 'NOT CONNECTED'}
                </span>
              </div>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                {isMetaLinked
                  ? 'Your Meta Business Suite is connected. You can re-authorize or change asset selections anytime below.'
                  : 'Link your Meta Business Suite so Dukene can publish your catalog updates automatically.'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleConnectMeta}
              disabled={!activeStoreId || isConnecting}
              className={`h-8 px-4 rounded-lg text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors shrink-0 ${
                isMetaLinked
                  ? 'border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#202223] dark:text-white'
                  : 'bg-[#006838] hover:bg-[#00522c] text-white'
              }`}
            >
              {isConnecting ? 'Connecting...' : isMetaLinked ? 'Re-authorize Meta Assets' : 'Connect Meta Assets'}
            </button>
          </div>

          {/* Connected Accounts Grid */}
          {isMetaLinked && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-[#202223] dark:text-white uppercase tracking-wide">Connected Accounts</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                {/* Facebook Page */}
                <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                      <FacebookLogo weight="fill" className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-bold text-[#202223] dark:text-white truncate">
                          {status?.pageName || 'Facebook Page'}
                        </span>
                        {status?.pageName && (
                          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-[#006838] shrink-0" />
                        )}
                      </div>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400">
                        {status?.pageId ? `Page ID: ${status.pageId}` : 'Facebook Page'}
                      </p>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                        Business ID: {status?.metaBusinessId || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E1E4E8] dark:border-slate-700 flex items-center justify-between">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status?.pageName ? 'bg-[#E3F1DF] text-[#006838]' : 'bg-amber-100 text-amber-800'}`}>
                      {status?.pageName ? 'CONNECTED' : 'NOT FOUND'}
                    </span>
                    <a
                      href={status?.pageId ? `https://www.facebook.com/${status.pageId}` : 'https://www.facebook.com'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline font-semibold"
                    >
                      <LinkSimple className="w-3 h-3" /> Open Page
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 flex items-center justify-center shrink-0">
                      <InstagramLogo weight="fill" className="w-5 h-5 text-pink-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-bold text-[#202223] dark:text-white">
                          Instagram Shopping
                        </span>
                        {status?.instagramLinked && (
                          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-[#006838] shrink-0" />
                        )}
                      </div>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400">
                        {status?.instagramId ? `Account ID: ${status.instagramId}` : 'Tag products in posts & stories'}
                      </p>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                        Catalog: {status?.metaCatalogId || 'Pending selection'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E1E4E8] dark:border-slate-700 flex items-center justify-between">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status?.instagramLinked ? 'bg-[#E3F1DF] text-[#006838]' : 'bg-amber-100 text-amber-800'}`}>
                      {status?.instagramLinked ? 'CONNECTED' : 'NOT LINKED'}
                    </span>
                    {status?.instagramLinked ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#006838] font-semibold">
                        <CheckCircle weight="fill" className="w-3 h-3" /> Shopping Active
                      </span>
                    ) : status?.instagramId ? (
                      <button
                        type="button"
                        onClick={() => linkInstagramMutation.mutate()}
                        disabled={!status?.metaCatalogId || linkInstagramMutation.isPending}
                        className="inline-flex items-center gap-1 text-[10px] text-pink-600 hover:underline font-semibold disabled:opacity-40"
                      >
                        {linkInstagramMutation.isPending ? 'Linking...' : 'Link Instagram Shopping'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleConnectMeta}
                        disabled={isConnecting}
                        className="inline-flex items-center gap-1 text-[10px] text-pink-600 hover:underline font-semibold"
                      >
                        Re-authorize to connect IG
                      </button>
                    )}
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center shrink-0">
                      <WhatsappLogo weight="fill" className="w-5 h-5 text-[#25D366]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-bold text-[#202223] dark:text-white">
                          WhatsApp Business
                        </span>
                        {status?.whatsappLinked && (
                          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-[#006838] shrink-0" />
                        )}
                      </div>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400">
                        {status?.whatsappId ? `WABA ID: ${status.whatsappId}` : 'WhatsApp Catalog Continuation'}
                      </p>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                        {status?.whatsappPhoneId ? `Phone ID: ${status.whatsappPhoneId}` : 'Share your catalog directly in chats'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E1E4E8] dark:border-slate-700 flex items-center justify-between">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status?.whatsappLinked ? 'bg-[#E3F1DF] text-[#006838]' : 'bg-amber-100 text-amber-800'}`}>
                      {status?.whatsappLinked ? 'CONNECTED' : 'READY TO LINK'}
                    </span>
                    {status?.whatsappLinked ? (
                      <button
                        type="button"
                        onClick={() => unlinkWhatsappMutation.mutate()}
                        disabled={unlinkWhatsappMutation.isPending}
                        className="inline-flex items-center gap-1 text-[10px] text-red-500 hover:underline font-semibold"
                      >
                        {unlinkWhatsappMutation.isPending ? 'Unlinking...' : 'Unlink'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => linkWhatsappMutation.mutate()}
                        disabled={!status?.metaCatalogId || linkWhatsappMutation.isPending}
                        className="inline-flex items-center gap-1 text-[10px] text-[#006838] hover:underline font-semibold disabled:opacity-40"
                      >
                        {linkWhatsappMutation.isPending ? 'Linking...' : 'Link WhatsApp'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Meta Catalog */}
                <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                      <ShoppingBag weight="fill" className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-bold text-[#202223] dark:text-white truncate">
                          {status?.catalogName || 'Product Catalog'}
                        </span>
                        {status?.metaCatalogId && (
                          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-[#006838] shrink-0" />
                        )}
                      </div>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400">
                        {status?.metaCatalogId ? `Catalog ID: ${status.metaCatalogId}` : 'No catalog linked yet'}
                      </p>
                      <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                        {status?.stats ? `${status.stats.synced}/${status.stats.total} products synced` : 'Sync products to populate this catalog'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E1E4E8] dark:border-slate-700 flex items-center justify-between">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status?.metaCatalogId ? 'bg-[#E3F1DF] text-[#006838]' : 'bg-amber-100 text-amber-800'}`}>
                      {status?.metaCatalogId ? 'ACTIVE' : 'NOT SELECTED'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsCatalogDialogOpen(true)}
                      className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline font-semibold"
                    >
                      {status?.metaCatalogId ? 'Change Catalog' : 'Select Catalog'}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Not connected CTA */}
          {!isMetaLinked && (
            <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AssetIcon src="/icons/ig-instagram-icon.svg" alt="Instagram" className="w-6 h-6" />
                <div>
                  <h4 className="text-xs font-bold text-[#202223] dark:text-white">
                    Instagram Shopping &amp; Catalog Tagging
                  </h4>
                  <p className="text-xs text-[#5C5F62]">
                    Tag products directly in stories, reels, and feed posts
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleConnectMeta}
                disabled={!activeStoreId || isConnecting}
                className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50"
              >
                {isConnecting ? 'Connecting...' : 'Connect Meta Assets'}
              </button>
            </div>
          )}

          {/* WhatsApp catalog continuation (existing large card for unconnected state) */}
          <div className="p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center text-[#006838] shrink-0">
                  <AssetIcon src="/icons/whatsapp-icon.svg" alt="WhatsApp" className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-bold text-[#202223] dark:text-white">
                    WhatsApp Catalog Continuation
                  </h4>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      isWhatsAppConnected
                        ? 'bg-[#E3F1DF] text-[#006838]'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {isWhatsAppConnected ? 'CONNECTED' : 'READY TO LINK'}
                  </span>
                </div>
                <p className="text-xs text-[#5C5F62] mt-1 max-w-2xl">
                  {isWhatsAppConnected
                    ? 'Your Meta catalog is already linked to WhatsApp. You can manage visibility, disconnect, or review the setup in the WhatsApp tab.'
                    : 'Once your Meta catalog is selected, Dukene will continue to WhatsApp linking using the same catalog. You can also do it manually here if needed.'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isWhatsAppConnected ? (
                <button
                  type="button"
                  onClick={() => setActiveTab('bot_flows')}
                  className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold"
                >
                  Open WhatsApp Tab
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => linkWhatsappMutation.mutate()}
                  disabled={!isMetaLinked || !status?.metaCatalogId || linkWhatsappMutation.isPending}
                  className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50"
                >
                  {linkWhatsappMutation.isPending ? 'Linking...' : 'Link WhatsApp Catalog'}
                </button>
              )}
            </div>
          </div>

          {isMetaLinked && !status?.metaCatalogId && (
            <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="text-xs font-bold text-amber-900 dark:text-amber-200">
                  Product Catalog Connection Required
                </h4>
                <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                  {availableCatalogs.length === 0
                    ? 'No product catalogs found under your Meta account right now. Click below to let Dukene automatically create and link a catalog for you!'
                    : `${availableCatalogs.length} product catalog(s) found. Please pick the one Dukene should sync your products to.`}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {availableCatalogs.length === 0 && (
                  <button
                    type="button"
                    disabled={createCatalogMutation.isPending}
                    onClick={() => createCatalogMutation.mutate({})}
                    className="h-8 px-3.5 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {createCatalogMutation.isPending ? 'Creating...' : 'Create Catalog Automatically'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsCatalogDialogOpen(true)}
                  className="h-8 px-3.5 rounded-lg border border-amber-600 dark:border-amber-400 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-xs font-semibold"
                >
                  {availableCatalogs.length === 0 ? 'Options & Manual Selection' : 'Select Catalog'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: MARKETING */}
      {activeTab === 'marketing' && (
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-bold text-[#202223] dark:text-white">
            Dynamic Facebook Ads & Pixel Tracking
          </h2>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400">
            Retarget shoppers who viewed products or abandoned their shopping cart with automated Facebook & Instagram ads.
          </p>
        </div>
      )}

      {/* TAB 4: WHATSAPP BOT FLOWS */}
      {activeTab === 'bot_flows' && (
        <div className="space-y-5">
          {/* WhatsApp Connection Banner inside Bot Flows Tab */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center text-[#006838] shrink-0">
                  <AssetIcon src="/icons/whatsapp-icon.svg" alt="WhatsApp" className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-[#202223] dark:text-white">
                    WhatsApp Business Phone Connection
                  </h3>
                  {status?.whatsappLinked ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F1DF] text-[#006838]">
                      <CheckCircle weight="fill" className="w-3.5 h-3.5" />
                      CONNECTED & ACTIVE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                      NUMBER CONNECTION REQUIRED
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-2xl">
                  {status?.whatsappLinked
                    ? 'Your WhatsApp Business phone number is linked. All Bot Flows configured below actively respond to incoming customer chats.'
                    : 'Connect your WhatsApp Business phone number right here to power automated sales & support. You can design your flows below now, and they will become live as soon as your number is linked.'}
                </p>
              </div>
            </div>

            <button
              type="button"
              disabled={linkWhatsappMutation.isPending || unlinkWhatsappMutation.isPending}
              onClick={async () => {
                if (status?.whatsappLinked) {
                  const confirmed = await confirmAction({
                    title: 'Disconnect WhatsApp Business',
                    message: 'Are you sure you want to disconnect your WhatsApp Business phone number?',
                    confirmText: 'Disconnect',
                    cancelText: 'Cancel',
                  });
                  if (confirmed) {
                    unlinkWhatsappMutation.mutate();
                  }
                } else {
                  linkWhatsappMutation.mutate();
                }
              }}
              className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold shrink-0 disabled:opacity-50"
            >
              {linkWhatsappMutation.isPending || unlinkWhatsappMutation.isPending
                ? 'Updating...'
                : status?.whatsappLinked
                ? 'Manage WhatsApp Number'
                : 'Connect WhatsApp Phone Number'}
            </button>
          </div>

          {/* Embedded Bot Flows Directory */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <BotFlowsList embedded />
          </div>
        </div>
      )}

      {/* TAB 5: SETTINGS */}
      {activeTab === 'settings' && (
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-bold text-[#202223] dark:text-white">
            Managed Meta Catalog & Business Asset IDs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3.5 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl border border-[#E1E4E8] dark:border-slate-800">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                Meta Catalog ID
              </span>
              <p className="text-xs font-mono font-bold text-[#202223] dark:text-white mt-1">
                {status?.metaCatalogId || 'Not connected'}
              </p>
            </div>

            <div className="p-3.5 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl border border-[#E1E4E8] dark:border-slate-800">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">
                Business Manager ID
              </span>
              <p className="text-xs font-mono font-bold text-[#202223] dark:text-white mt-1">
                {status?.metaBusinessId || 'Not connected'}
              </p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={isCatalogDialogOpen} onOpenChange={handleCatalogDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select a Meta Catalog</DialogTitle>
            <DialogDescription>
              Choose which Meta product catalog Dukene should sync to. If you only have one catalog, it can be selected automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-72 overflow-auto pr-1">
            {availableCatalogs.length === 0 ? (
              <div className="p-4 rounded-xl border border-dashed border-[#D2D5D9] dark:border-slate-700 bg-[#F6F6F7] dark:bg-slate-900 text-center space-y-3">
                <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                  No existing product catalogs found under your Meta account right now.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={createCatalogMutation.isPending}
                    onClick={() => createCatalogMutation.mutate({})}
                    className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {createCatalogMutation.isPending ? 'Creating...' : 'Create Catalog Automatically'}
                  </button>
                  <a
                    href="https://business.facebook.com/commerce"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 px-4 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 text-[#202223] dark:text-white text-xs font-semibold inline-flex items-center"
                  >
                    Open Commerce Manager
                  </a>
                </div>
              </div>
            ) : (
              availableCatalogs.map((catalog) => (
                <button
                  key={catalog.id}
                  type="button"
                  onClick={() => setSelectedCatalogId(catalog.id)}
                  className={`w-full text-left rounded-xl border px-3 py-3 transition-colors ${
                    selectedCatalogId === catalog.id
                      ? 'border-[#006838] bg-[#E3F1DF]'
                      : 'border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-[#202223] dark:text-white">
                        {catalog.name || 'Unnamed catalog'}
                      </p>
                      <p className="text-[11px] text-[#5C5F62] mt-0.5">ID: {catalog.id}</p>
                    </div>
                    <span className="text-[11px] text-[#5C5F62]">
                      {typeof catalog.product_count === 'number' ? `${catalog.product_count} products` : 'Catalog'}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => handleCatalogDialogOpenChange(false)}
              className="h-8 px-4 rounded-lg border border-[#D2D5D9] dark:border-slate-700 text-xs font-semibold text-[#202223] dark:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedCatalogId || selectCatalogMutation.isPending}
              onClick={() => {
                const catalog = availableCatalogs.find((item) => item.id === selectedCatalogId);
                if (!catalog) return;
                selectCatalogMutation.mutate({
                  catalogId: catalog.id,
                  catalogName: catalog.name,
                });
              }}
              className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50"
            >
              {selectCatalogMutation.isPending ? 'Saving...' : 'Save Catalog'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
